/**
 * Notification Helpers
 * Utility functions for creating notifications across the application
 * Persisted notifications should represent meaningful user-facing events.
 * Local-only UI feedback should stay in NotificationService toast/template flows.
 */

import { Notification } from '../types/notifications';
import { DataService } from '../services/DataService';
import { NotificationService } from '../services/NotificationService';
import { buildProjectPathById } from './prettyRouteBuilders';

interface NotificationParams {
  userId: string;
  type: Notification['type'];
  priority: Notification['priority'];
  title: string;
  message: string;
  actionUrl?: string;
  actionLabel?: string;
  relatedId?: string;
  relatedEntityId?: string;
  relatedEntityType?: Notification['relatedEntityType'];
  projectId?: string;
  projectName?: string;
  companyId?: string;
  /** ISO string to use as createdAt instead of now() — callers that know the
   *  real event time (e.g. email internalDate) should pass it so the
   *  notification shows the correct relative age. */
  createdAt?: string;
  metadata?: Record<string, any>;
}

/**
 * Create a notification for a user (backend-persisted)
 */
export async function createNotification(params: NotificationParams): Promise<void> {
  try {
    await NotificationService.createNotification({
      userId: params.userId,
      type: params.type,
      priority: params.priority,
      title: params.title,
      message: params.message,
      actionUrl: params.actionUrl,
      actionLabel: params.actionLabel,
      relatedEntityId: params.relatedEntityId || params.relatedId,
      relatedId: params.relatedId || params.relatedEntityId,
      relatedEntityType: params.relatedEntityType,
      projectId: params.projectId,
      projectName: params.projectName,
      companyId: params.companyId,
      createdAt: params.createdAt,
      metadata: params.metadata,
      read: false,
    });
  } catch (error) {
    console.error('Failed to create notification:', error);
  }
}

/**
 * Notify team members when a new contract item is created (CVI, RFI, EOT, Variation)
 */
export function notifyContractItemCreated(
  itemType: 'CVI' | 'RFI' | 'EOT' | 'Variation' | 'Payment Application',
  itemReference: string,
  projectName: string,
  projectId: string,
  createdByName: string,
  assignedUsers: string[],
  itemId?: string
): void {
  const typeLabels: Record<string, string> = {
    'CVI': 'Compensation Event',
    'RFI': 'Request for Information',
    'EOT': 'Extension of Time',
    'Variation': 'Variation',
    'Payment Application': 'Payment Application',
  };

  // Validate user IDs before creating notifications
  const allUsers = DataService.getUsers() as Array<{ id: string }>;
  const validUserIds = new Set(allUsers.map((u) => u.id));
  
  const validAssignedUsers = assignedUsers.filter(userId => {
    const isValid = validUserIds.has(userId);
    if (!isValid) {
      console.warn(`⚠️ Invalid user ID in notifyContractItemCreated: "${userId}" - skipping notification`);
    }
    return isValid;
  });

  validAssignedUsers.forEach(userId => {
    createNotification({
      userId,
      type: 'approval_required',
      priority: 'high',
      title: `New ${typeLabels[itemType]} Created`,
      message: `${createdByName} created ${itemReference} for ${projectName}`,
      actionUrl: buildProjectPathById(projectId),
      projectId,
      relatedEntityId: itemId,
      relatedEntityType: itemType === 'Payment Application' ? 'application' : 'contract-item',
      metadata: {
        itemType: itemType,
        itemReference: itemReference,
        itemId: itemId,
      },
    });
  });
}

/**
 * Notify when a payment is due
 */
export function notifyPaymentDue(
  applicationNumber: string,
  amount: number,
  dueDate: string,
  projectName: string,
  projectId: string,
  notifyUsers: string[],
  applicationId?: string
): void {
  notifyUsers.forEach(userId => {
    createNotification({
      userId,
      type: 'payment_due',
      priority: 'high',
      title: 'Payment Application Due',
      message: `Application ${applicationNumber} for ${projectName} - £${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} due ${dueDate}`,
      actionUrl: buildProjectPathById(projectId),
      projectId,
      relatedEntityId: applicationId,
      relatedEntityType: 'application',
      metadata: {
        applicationNumber: applicationNumber,
        applicationId: applicationId,
        amount: amount,
        dueDate: dueDate,
      },
    });
  });
}

/**
 * Notify when a BOQ item requires approval
 */
export function notifyBOQItemApproval(
  itemDescription: string,
  createdByName: string,
  projectName: string,
  projectId: string,
  notifyUsers: string[]
): void {
  notifyUsers.forEach(userId => {
    createNotification({
      userId,
      type: 'approval_required',
      priority: 'medium',
      title: 'BOQ Item Requires Approval',
      message: `${createdByName} added "${itemDescription}" to ${projectName}`,
      actionUrl: buildProjectPathById(projectId),
      projectId,
    });
  });
}

/**
 * Get project team member IDs
 * Validates that the IDs exist in the users table to prevent foreign key errors
 */
export function getProjectTeamUserIds(projectId: string): string[] {
  const project = DataService.getProject(projectId);
  if (!project) return [];
  
  const teamIds: string[] = [];
  
  // Use assignedStaffIds which contains actual user IDs
  if (project.assignedStaffIds && project.assignedStaffIds.length > 0) {
    teamIds.push(...project.assignedStaffIds);
  }
  
  // Also include supervisor IDs if present
  if (project.assignedSupervisorIds && project.assignedSupervisorIds.length > 0) {
    teamIds.push(...project.assignedSupervisorIds);
  }
  
  // Validate that all IDs exist in the users table
  const allUsers = DataService.getUsers() as Array<{ id: string }>;
  const validUserIds = new Set(allUsers.map((u) => u.id));
  
  const validatedIds = [...new Set(teamIds)].filter(id => {
    const isValid = validUserIds.has(id);
    if (!isValid) {
      console.warn(`⚠️ Invalid user ID found in project team: "${id}" - skipping notification for this user`);
    }
    return isValid;
  });
  
  return validatedIds;
}
